// CotizacionesListXL - Sprint 44 Fase 3 Vista 4.
// Lista de cotizaciones XL. Reemplaza cotizaciones/list/CotizacionesList.
// Filtros (busqueda debounced, estado pills XLChip) + XLTable + pager +
// msg auto-dismiss.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ensureKeyframes, injectCSS } from '../../design/motion';
import useCotizacionesListXL from './hooks/useCotizacionesListXL';
import XLTable from '../../components/xl/XLTable';
import XLInput from '../../components/xl/XLInput';
import XLBadge from '../../components/xl/XLBadge';
import XLChip from '../../components/xl/XLChip';
import { fmtMoney } from '../../utils/money';

const CSS_ID = 'xl-cot-list-css';
const CSS = `
.xl-page { padding: 24px; font-family: var(--xl-font-sans); color: var(--xl-color-text) }
.xl-page__head {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; margin-bottom: 16px; flex-wrap: wrap;
}
.xl-page__title { font-size: 22px; font-weight: 600; margin: 0 }
.xl-page__subtitle { font-size: 13px; color: var(--xl-color-text-secondary); margin: 2px 0 0 }

.xl-btn {
  font: 500 13px/1 var(--xl-font-sans); padding: 10px 16px;
  border-radius: var(--xl-radius-md); border: 1px solid transparent; cursor: pointer;
}
.xl-btn:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
.xl-btn:disabled { opacity: 0.45; cursor: not-allowed }
.xl-btn--primary { background: var(--xl-color-primary); color: var(--xl-color-text-inverse) }
.xl-btn--primary:hover { background: var(--xl-color-primary-hover) }
.xl-btn--ghost {
  background: transparent; color: var(--xl-color-text-secondary);
  border-color: var(--xl-color-border);
}
.xl-btn--ghost:hover { background: var(--xl-color-surface); color: var(--xl-color-text) }

.xl-filters {
  background: var(--xl-color-surface); border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg); padding: 16px; margin-bottom: 16px;
  box-shadow: var(--xl-shadow-sm);
  display: flex; flex-direction: column; gap: 12px;
}
.xl-pills { display: flex; flex-wrap: wrap; gap: 6px }
.xl-banner {
  padding: 10px 14px; margin-bottom: 12px; border-radius: var(--xl-radius-md); font-size: 13px;
}
.xl-banner--err {
  background: rgba(220,38,38,0.10); color: var(--xl-color-danger);
  border: 1px solid rgba(220,38,38,0.28);
}

.xl-pager {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; padding: 12px 4px; margin-top: 12px;
  color: var(--xl-color-text-secondary); font-size: 13px;
}
.xl-pager__group { display: flex; gap: 8px; align-items: center }
.xl-mono { font-family: var(--xl-font-mono); font-size: 12px }
`;

const ESTADO_VARIANT = {
  borrador: 'neutral', enviada: 'info', aceptada: 'success', rechazada: 'danger',
};
const ESTADO_LABEL = {
  borrador: 'Borrador', enviada: 'Enviada', aceptada: 'Aceptada', rechazada: 'Rechazada',
};
const ESTADO_FILTROS = [
  { value: '',          label: 'Todas' },
  { value: 'borrador',  label: 'Borrador' },
  { value: 'enviada',   label: 'Enviada' },
  { value: 'aceptada',  label: 'Aceptada' },
  { value: 'rechazada', label: 'Rechazada' },
];

function useDebouncedSetter(setter, delay) {
  const [local, setLocal] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setter(local), delay);
    return () => clearTimeout(t);
  }, [local, delay, setter]);
  return [local, setLocal];
}

export default function CotizacionesListXL() {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);

  const navigate = useNavigate();
  const cx = useCotizacionesListXL();
  const [searchLocal, setSearchLocal] = useDebouncedSetter(cx.setQ, 300);

  const columns = useMemo(() => ([
    { key: 'numero', label: 'Numero',
      render: (r) => <span className="xl-mono">{r.numero}</span> },
    { key: 'cliente', label: 'Cliente',
      render: (r) => (r.cliente && r.cliente.nombre) || '\u2014' },
    { key: 'fecha', label: 'Fecha',
      render: (r) => r.fecha || '\u2014' },
    { key: 'total', label: 'Total', align: 'right',
      render: (r) => (
        <span className="xl-mono">{fmtMoney(r.total, r.moneda)}</span>
      ) },
    { key: 'estado', label: 'Estado',
      render: (r) => (
        <XLBadge variant={ESTADO_VARIANT[r.estado] || 'neutral'} dot>
          {ESTADO_LABEL[r.estado] || r.estado}
        </XLBadge>
      ) },
  ]), []);

  const { page, totalPages, total, canPrev, canNext, loading } = cx;

  return (
    <div className="xl-page">
      <header className="xl-page__head">
        <div>
          <h2 className="xl-page__title">Cotizaciones</h2>
          <p className="xl-page__subtitle">Propuestas comerciales.</p>
        </div>
        <div>
          <button type="button" className="xl-btn xl-btn--primary"
            onClick={() => navigate('/cotizaciones/nueva')}>
            + Nueva cotizacion
          </button>
        </div>
      </header>

      <section className="xl-filters" aria-label="Filtros de cotizaciones">
        <div className="xl-pills" role="tablist" aria-label="Estado">
          {ESTADO_FILTROS.map((f) => (
            <XLChip
              key={f.value || 'all'}
              onClick={() => cx.setEstado(f.value)}
              selected={cx.estado === f.value}
            >
              {f.label}
            </XLChip>
          ))}
        </div>
        <XLInput
          type="search"
          label="Buscar"
          placeholder="Numero o cliente..."
          value={searchLocal}
          onChange={(e) => setSearchLocal(e.target.value)}
        />
      </section>

      {cx.err && (
        <div className="xl-banner xl-banner--err" role="alert">{cx.err}</div>
      )}

      <XLTable
        ariaLabel="Lista de cotizaciones"
        columns={columns}
        data={cx.items}
        loading={loading}
        onRowClick={(r) => navigate('/cotizaciones/' + r.id)}
        rowKey="id"
        emptyMessage="Sin cotizaciones para estos filtros."
      />

      <div className="xl-pager">
        <span>
          {total
            ? ('Pagina ' + page + ' de ' + totalPages + ' \u00b7 ' + total + ' resultados')
            : 'Sin resultados'}
        </span>
        <span className="xl-pager__group">
          <button type="button" className="xl-btn xl-btn--ghost"
            disabled={!canPrev || loading}
            onClick={() => cx.setPage(page - 1)}>
            Anterior
          </button>
          <span className="xl-mono">{page} / {totalPages}</span>
          <button type="button" className="xl-btn xl-btn--ghost"
            disabled={!canNext || loading}
            onClick={() => cx.setPage(page + 1)}>
            Siguiente
          </button>
        </span>
      </div>
    </div>
  );
}
